/**
 * useUsageSnapshot — one-shot read of the org's live usage numbers.
 *
 * PLANS-1B extends the SUB-1 Phase 3B snapshot with client + project
 * bands. The `my_usage_snapshot` view (PLANS-1A) returns:
 *
 *   { org_id, plan,
 *     storage_used_bytes, storage_cap_bytes,
 *     nova_actions_this_month, nova_cap_this_month,
 *     team_cap, team_current,
 *     client_cap, client_current,
 *     project_cap, project_current }
 *
 * Nova cap fields stay on the view for backend analytics but the
 * visible UI has been killed — Nova is unlimited on every paid tier
 * post-PLANS-1. The band still exposes them for any surface that
 * wants to know (e.g., analytics dashboards); it just never renders.
 *
 * RLS on the view scopes to auth.uid()'s org, so a client-side
 * `.from().single()` is safe and cheaper than an edge fn call.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useCheckSubscription, type SubscriptionPlan } from '@/services/billing';

interface RawUsageSnapshot {
  org_id: string;
  plan: SubscriptionPlan;
  storage_used_bytes: number;
  storage_cap_bytes: number;
  nova_actions_this_month: number;
  nova_cap_this_month: number;
  team_cap: number;
  team_current: number;
  client_cap: number | null;
  client_current: number | null;
  project_cap: number | null;
  project_current: number | null;
}

export interface UsageBand {
  used: number;
  cap: number;
  /** 0–100. Capped at 100 for the display bar (isOverLimit tracks the true state). */
  percent: number;
  isNearLimit: boolean; // >= 75%
  isAtLimit: boolean;   // >= 90%
  isOverLimit: boolean; // >= 100%
}

export interface StorageBand extends Omit<UsageBand, 'used' | 'cap'> {
  usedBytes: number;
  capBytes: number;
  /** True when the plan is orbit_business — treat cap as reasonable-use unlimited. */
  isUnlimited: boolean;
}

export interface NovaBand extends UsageBand {
  /** ISO string for the first of the next UTC month — when the counter rolls back to 0. */
  resetAt: string;
  /** PLANS-1: Nova is unlimited on every paid tier. Kept for legacy consumers. */
  isUnlimited: boolean;
}

export interface TeamBand {
  current: number;
  cap: number;
  /** True when the plan is orbit_business — cap is effectively unlimited. */
  isUnlimited: boolean;
  isAtLimit: boolean; // current >= cap
}

/**
 * PLANS-1B — client + project bands. Only Starter carries real caps
 * (15 clients / 5 active projects). Orbit + Business are unlimited so
 * `isUnlimited` short-circuits every threshold flag.
 */
export interface CountBand {
  current: number;
  cap: number;
  isUnlimited: boolean;
  /** 0–100, capped at 100. */
  percent: number;
  isNearLimit: boolean; // >= 75%
  isAtLimit: boolean;   // current >= cap
  isOverLimit: boolean; // current > cap (only possible via legacy data)
}

export interface UsageSnapshot {
  plan: SubscriptionPlan;
  storage: StorageBand;
  novaActions: NovaBand;
  team: TeamBand;
  clients: CountBand;
  projects: CountBand;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

// The migration parks orbit_business's storage cap at int8 MAX and
// team/cap counters at int4 MAX to keep predicate math simple
// server-side. Client-side we detect those sentinel values and render
// "Unlimited".
const INT8_MAX = 9223372036854775807n;
const INT4_MAX = 2147483647;

function clampPercent(used: number, cap: number): number {
  if (cap <= 0) return used > 0 ? 100 : 0;
  return Math.min(100, Math.max(0, Math.round((used / cap) * 100)));
}

function firstOfNextUtcMonth(): string {
  const now = new Date();
  const next = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth() + 1,
      1,
      0,
      0,
      0,
      0,
    ),
  );
  return next.toISOString();
}

function buildCountBand(
  current: number,
  cap: number,
  plan: SubscriptionPlan,
): CountBand {
  const isUnlimited =
    plan === 'orbit' || plan === 'orbit_business' || cap >= INT4_MAX;
  const percent = isUnlimited ? 0 : clampPercent(current, cap);
  return {
    current,
    cap,
    isUnlimited,
    percent,
    isNearLimit: !isUnlimited && percent >= 75,
    isAtLimit: !isUnlimited && current >= cap,
    isOverLimit: !isUnlimited && current > cap,
  };
}

export function useUsageSnapshot(): UsageSnapshot {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: subscriptionData } = useCheckSubscription();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['usage-snapshot', user?.id],
    queryFn: async (): Promise<RawUsageSnapshot | null> => {
      // The view isn't in the generated types yet — cast through
      // unknown so TS doesn't complain while still keeping the runtime
      // shape safe.
      const { data, error } = await (supabase as unknown as {
        from: (t: string) => {
          select: (q: string) => {
            single: () => Promise<{ data: RawUsageSnapshot | null; error: unknown }>;
          };
        };
      })
        .from('my_usage_snapshot')
        .select('*')
        .single();

      if (error) {
        // Fresh signup: no profile row yet, or view returns nothing.
        // Return null so the hook falls back to defaults instead of
        // blocking the whole app on a usage query.
        return null;
      }
      return data ?? null;
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 60s
    refetchOnWindowFocus: true,
  });

  // Any change in the subscription plan → the caps changed, so we
  // want the fresh numbers immediately (not up to 60s stale).
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ['usage-snapshot', user?.id] });
    // subscriptionData?.plan is the trigger; other fields don't
    // affect usage caps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData?.plan, user?.id]);

  const plan: SubscriptionPlan = data?.plan ?? subscriptionData?.plan ?? 'free';

  const usedBytes = data?.storage_used_bytes ?? 0;
  const capBytes = data?.storage_cap_bytes ?? 0;
  const storageUnlimited = plan === 'orbit_business' || BigInt(capBytes || 0) >= INT8_MAX;
  const storagePercent = storageUnlimited ? 0 : clampPercent(usedBytes, capBytes);
  const storage: StorageBand = {
    usedBytes,
    capBytes,
    percent: storagePercent,
    isNearLimit: !storageUnlimited && storagePercent >= 75,
    isAtLimit: !storageUnlimited && storagePercent >= 90,
    isOverLimit: !storageUnlimited && capBytes > 0 && usedBytes >= capBytes,
    isUnlimited: storageUnlimited,
  };

  // PLANS-1: Nova is unlimited on every paid tier. The counter still
  // flows through so backend analytics + admin tools can see it, but
  // consumer-facing UI treats Free as the only capped state.
  const novaUsed = data?.nova_actions_this_month ?? 0;
  const novaCap = data?.nova_cap_this_month ?? 0;
  const novaUnlimited = plan !== 'free';
  const novaPercent = novaUnlimited ? 0 : clampPercent(novaUsed, novaCap);
  const novaActions: NovaBand = {
    used: novaUsed,
    cap: novaCap,
    percent: novaPercent,
    isNearLimit: !novaUnlimited && novaCap > 0 && novaPercent >= 75,
    isAtLimit: !novaUnlimited && novaCap > 0 && novaPercent >= 90,
    isOverLimit: !novaUnlimited && (novaCap === 0 || novaUsed >= novaCap),
    resetAt: firstOfNextUtcMonth(),
    isUnlimited: novaUnlimited,
  };

  const teamCurrent = data?.team_current ?? 1;
  const teamCap = data?.team_cap ?? 1;
  const teamUnlimited = plan === 'orbit_business' || teamCap >= INT4_MAX;
  const team: TeamBand = {
    current: teamCurrent,
    cap: teamCap,
    isUnlimited: teamUnlimited,
    isAtLimit: !teamUnlimited && teamCurrent >= teamCap,
  };

  const clients = buildCountBand(
    data?.client_current ?? 0,
    data?.client_cap ?? INT4_MAX,
    plan,
  );
  const projects = buildCountBand(
    data?.project_current ?? 0,
    data?.project_cap ?? INT4_MAX,
    plan,
  );

  return {
    plan,
    storage,
    novaActions,
    team,
    clients,
    projects,
    isLoading: isLoading && !!user,
    isError,
    refetch,
  };
}

/**
 * Human-readable byte formatter. Never returns "0 KB" for tiny files
 * — bumps up to MB / GB / TB as usage grows so the bar copy reads
 * naturally at every scale.
 */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '0 MB';
  const kb = bytes / 1024;
  const mb = kb / 1024;
  const gb = mb / 1024;
  const tb = gb / 1024;
  if (tb >= 1) return `${tb.toFixed(tb >= 10 ? 0 : 1)} TB`;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
  return `${Math.max(1, Math.round(kb))} KB`;
}
